<template>
  <el-drawer
    v-model="visible"
    title="编辑轮播图"
    direction="rtl"
    size="50%"
    :destroy-on-close="true"
    @close="handleClose"
  >
    <el-form
      :model="bannerForm"
      :rules="rules"
      ref="bannerFormRef"
      label-position="top"
      label-width="120px"
    >
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="标题" prop="title">
            <el-input v-model="bannerForm.title" placeholder="请输入标题" />
          </el-form-item>
        </el-col>
      </el-row>
       <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="轮播图片" prop="image_url">
            <!-- 图片大小提示 -->
            <el-alert
              title="图片上传提示"
              type="info"
              :closable="false"
              show-icon
              style="margin-bottom: 16px;"
            >
              <template #default>
                <div>基于当前服务器配置，建议您将上传的图片大小控制在200KB以下，以确保页面加载速度和最佳显示效果。</div>
              </template>
            </el-alert>
            
            <el-upload
              :file-list="imageFileList"
              :action="`${apiBaseUrl}/manage/upload/image`"
              list-type="picture-card"
              :auto-upload="true"
              :on-preview="handlePictureCardPreview"
              :on-remove="handleRemove"
              :on-success="handleImageUploadSuccess"
              :on-error="handleImageUploadError"
              :before-upload="beforeImageUpload"
              :data="{ type: 'content' }"
              :headers="{ 'Authorization': `Bearer ${authToken}` }"
            >
              <el-icon><plus /></el-icon>
              <template #tip>
                <div class="el-upload__tip">
                  只能上传jpg/png文件，且不超过20MB
                </div>
              </template>
            </el-upload>
            <el-dialog v-model="previewVisible" title="预览图片">
              <img style="width: 100%" :src="dialogImageUrl" alt="预览图片" />
            </el-dialog>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="状态" prop="status">
            <el-radio-group v-model="bannerForm.status">
              <el-radio label="active">启用</el-radio>
              <el-radio label="inactive">禁用</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="排序" prop="sort_order">
            <el-input v-model="bannerForm.sort_order" type="number" placeholder="请输入排序号" />
          </el-form-item>
        </el-col>
      </el-row>
      
      <el-row :gutter="20">
        <el-col :span="24">
          <el-form-item label="描述" prop="description">
            <el-input v-model="bannerForm.description" type="textarea" placeholder="请输入描述" rows="4" />
          </el-form-item>
        </el-col>
      </el-row>
      
      <div class="form-actions">
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="handleSubmit">提交</el-button>
      </div>
    </el-form>
  </el-drawer>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { ElMessage } from 'element-plus';
import { Plus } from '@element-plus/icons-vue';
import request from '../../api/axios';

export default {
  name: 'BannerEdit',
  components: {
    Plus
  },
  setup() {
    // 获取API基础URL，与axios配置保持一致
    const apiBaseUrl = ref(process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api');
    
    const router = useRouter();
    const route = useRoute();
    const bannerFormRef = ref(null);
    const bannerId = route.params.id;
    
    // 抽屉显示状态
    const visible = ref(true);
    
    const bannerForm = ref({
      title: '',
      image_url: '',
      status: 'active',
      sort_order: 0,
      description: ''
    });
    
    // 图片上传相关变量
    const imageFileList = ref([]);
    const previewVisible = ref(false);
    const dialogImageUrl = ref('');
    
    // 获取认证token
    const getToken = () => {
      try {
        return localStorage.getItem('token') || '';
      } catch (e) {
        console.error('Failed to access localStorage:', e);
        return '';
      }
    };
    
    const authToken = ref(getToken());
    
    const rules = {
      title: [
        { required: true, message: '请输入标题', trigger: 'blur' }
      ],
      image_url: [
        { required: true, message: '请上传轮播图片', trigger: 'change' }
      ]
    };
    
    // 获取轮播图详情
    const fetchBannerDetail = async () => {
      try {
        const response = await request({
          url: `/manage/banners/${bannerId}`,
          method: 'get'
        });
        bannerForm.value = response.data;
        
        // 初始化图片文件列表
        if (response.data.image_url) {
          imageFileList.value = [{
            uid: Date.now(), // 添加必需的uid字段
            name: '轮播图',
            url: response.data.image_url,
            status: 'success' // 添加状态字段
          }];
        }
      } catch (error) {
        ElMessage.error('获取轮播图详情失败');
        console.error(error);
        handleClose();
      }
    };
    
    // 页面加载时获取轮播图详情
    onMounted(() => {
      fetchBannerDetail();
    });
    
    // 提交表单
    const handleSubmit = async () => {
      if (!bannerFormRef.value) return;
      
      await bannerFormRef.value.validate(async (valid) => {
        if (valid) {
          try {
            await request({
              url: `/manage/banners/${bannerId}`,
              method: 'put',
              data: bannerForm.value
            });
            ElMessage.success('轮播图更新成功');
            // 添加延迟确保组件状态稳定后再导航，避免卸载时的错误
            setTimeout(() => {
              handleClose();
            }, 0);
          } catch (error) {
            ElMessage.error('轮播图更新失败');
            console.error(error);
          }
        } else {
          return false;
        }
      });
    };
    
    // 取消操作
    const handleCancel = () => {
      handleClose();
    };
    
    // 关闭抽屉
    const handleClose = () => {
      visible.value = false;
      // 添加延迟确保组件状态稳定后再导航，避免卸载时的错误
      setTimeout(() => {
        router.push('/contents/banners');
      }, 300);
    };
    
    // 图片上传相关函数
    // 预览图片
    const handlePictureCardPreview = (file) => {
      dialogImageUrl.value = file.url;
      previewVisible.value = true;
    };
    
    // 移除图片
    const handleRemove = () => {
      imageFileList.value = [];
      bannerForm.value.image_url = '';
    };
    
    // 图片上传前的验证
    const beforeImageUpload = (file) => {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png';
      const isLt20M = file.size / 1024 / 1024 < 20;
      
      if (!isJPG) {
        ElMessage.error('只能上传JPG/PNG格式的图片');
      }
      if (!isLt20M) {
        ElMessage.error('图片大小不能超过20MB');
      }
      
      return isJPG && isLt20M;
    };
    
    // 图片上传成功回调
    const handleImageUploadSuccess = (response, file, fileList) => {
      // 处理后端返回的errno格式
      if (response.errno === 0) {
        // 确保返回的是完整的图片URL
        let imgUrl = response.data[0].url;
        // 检查URL是否是完整的http/https地址
        if (imgUrl && !imgUrl.startsWith('http://') && !imgUrl.startsWith('https://')) {
          // 如果是相对路径，添加完整域名
          imgUrl = `${window.location.origin}${imgUrl}`;
        }
        // 上传成功，设置轮播图图片URL
        bannerForm.value.image_url = imgUrl;
        // 更新文件列表，确保文件列表中的图片URL也是完整的
        const updatedFileList = fileList.map(item => {
          if (item.response && item.response.errno === 0 && item.response.data && item.response.data.length > 0) {
            let fileUrl = item.response.data[0].url;
            if (fileUrl && !fileUrl.startsWith('http://') && !fileUrl.startsWith('https://')) {
              fileUrl = `${window.location.origin}${fileUrl}`;
            }
            return {
              ...item,
              url: fileUrl
            };
          }
          return item;
        });
        imageFileList.value = updatedFileList;
        ElMessage.success('图片上传成功');
      } else {
        ElMessage.error('图片上传失败: ' + (response.message || '未知错误'));
      }
    };
    
    // 图片上传失败回调
    // eslint-disable-next-line no-unused-vars
    const handleImageUploadError = (error, file, fileList) => {
      // 尝试从error对象中获取错误信息，处理各种可能的error类型
      let errorMessage = '网络错误';
      if (error) {
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error.response && error.response.data) {
          // 处理axios错误对象
          errorMessage = error.response.data.message || '未知错误';
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.statusText) {
          errorMessage = error.statusText;
        }
      }
      ElMessage.error('图片上传失败: ' + errorMessage);
    };
    
    return {
      apiBaseUrl,
      visible,
      bannerForm,
      bannerFormRef,
      imageFileList,
      previewVisible,
      dialogImageUrl,
      authToken,
      rules,
      handleSubmit,
      handleCancel,
      handleClose,
      handlePictureCardPreview,
      handleRemove,
      beforeImageUpload,
      handleImageUploadSuccess,
      handleImageUploadError
    };
  }
};
</script>

<style scoped>
/* 抽屉内样式 */
.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
}

.form-actions .el-button {
  margin-left: 10px;
}
</style>